import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

export interface IRainToolCallsToggleProps {
  /** Whether the thread's tool calls are currently listed. */
  showToolCalls: boolean;
  onShowToolCallsChange: (next: boolean) => void;
}

/**
 * Shows or hides the thread's tool calls.
 *
 * A HARD FILTER, as in rain's own UI: turning it off removes the calls from the
 * transcript rather than collapsing them, because someone who wants to read the
 * conversation is not helped by a column of headline rows. The specially
 * handled calls stay regardless — see the filter in `RainChatView`.
 *
 * Defaults to ON, since the surfaces that mount this exist to show the agent's
 * working.
 */
export function RainToolCallsToggle({
  showToolCalls,
  onShowToolCallsChange,
}: IRainToolCallsToggleProps) {
  const { t } = useTranslation('rain');

  const label = showToolCalls
    ? t('rain.toolCalls.hide')
    : t('rain.toolCalls.show');

  return (
    <Flex justify="end" width="full" className="shrink-0 px-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            aria-pressed={showToolCalls}
            onClick={() => onShowToolCallsChange(!showToolCalls)}
            data-testid="rain-tool-calls-toggle"
          >
            {/*
             * `Wrench` is the glyph the tool-call cards themselves carry, so the
             * control always reads as "this is about tool calls"; the eye says
             * which way it will go. `MessageCircle` was the first choice and it
             * was wrong — it reads as "chat", not "reveal the tools".
             */}
            <Icon type={showToolCalls ? 'EyeOff' : 'Wrench'} size="sm" />
            {label}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('rain.toolCalls.tooltip')}</TooltipContent>
      </Tooltip>
    </Flex>
  );
}
